"use client";

import {
  getPublicStyleByServiceCabapType,
  getServiceCapabTypeFromValue,
} from "components/molecules/PartnerProductsTable/auxFunctions";
import ProductsTooltip from "components/molecules/Tooltips/ProductsTooltip";
import CybersecurityInfo from "components/organisms/CybersecurityInfo";
import EnterpriseSoftwareInfo from "components/organisms/EnterpriseSoftwareInfo";
import MainframeSolutionInfo from "components/organisms/MainframeInfo";
import { useTranslation } from "react-i18next";

import { CustomAccordion } from "./Accordion";
import styles from "./style.module.scss";

function removeDuplicatesFromArray(data: string[]) {
  return data.filter((value, index) => data.indexOf(value) === index);
}

export default function PartnerServiceCapabilities({
  broadcomProducts,
  partner,
  serviceOfferings,
  withUseCasesAndVerticals = false,
  productsToServices,
  servicesToProducts,
}) {
  const { t } = useTranslation();

  const getTooltipData = (name, isServiceOffering = false) => {
    if (isServiceOffering) {
      if (servicesToProducts?.find((e) => e.name === name)) {
        return (
          <div
            className={`${styles.tooltip_wrapper} grid_container two_columns`}
          >
            <CustomAccordion
              data={
                removeDuplicatesFromArray(
                  servicesToProducts?.find((prod) => name === prod.name)
                    .products
                ) || []
              }
              title={t("products")}
            />
            <CustomAccordion
              data={
                removeDuplicatesFromArray(
                  servicesToProducts
                    .find((e) => e.name === name)
                    ?.locations.map((e) => e.name)
                ) || []
              }
              isText
              title={t("geo_cover")}
            />
            <CustomAccordion
              data={
                removeDuplicatesFromArray(
                  servicesToProducts
                    ?.find((e) => e.name === name)
                    ?.languages.map((e) => e.name)
                ) || []
              }
              isText
              title={t("languages")}
            />
          </div>
        );
      }
      return <></>;
    }

    const prodsToConsider = productsToServices.find((e) => e.name === name);
    return (
      <div className={`${styles.tooltip_wrapper} grid_container two_columns`}>
        <CustomAccordion
          data={prodsToConsider.services.map((e) => e.name)}
          title={t("service_offerings")}
        />
        <CustomAccordion
          data={prodsToConsider.locations.map((e) => e.name)}
          isText
          title={t("geo_cover")}
        />
        <CustomAccordion
          data={prodsToConsider.languages.map((e) => e.name)}
          isText
          title={t("languages")}
        />
        {!!prodsToConsider?.certification?.length ? (
          <CustomAccordion
            data={prodsToConsider.certification.map((e) => {
              return {
                title: e.value,
                items: e.items,
              };
            })}
            isMultiLevel
            title={t("tech_certification")}
          />
        ) : null}
      </div>
    );
  };

  const [noEducation, education] = serviceOfferings.reduce(
    (result, element) => {
      result[
        !["Education Delivery", "Education Reseller"].includes(element.name)
          ? 0
          : 1
      ].push(element);
      return result;
    },
    [[], []]
  );

  const serviceTypes = Array.from(
    new Set(partner.capabilities?.map((e) => e.serviceType))
  )?.sort((a: string, b: string) => a.localeCompare(b));

  const getProdCapab = ({ capab, serviceType, index, a }) => {
    return (
      capab.filter((p) => p.area === a.level1 && !p.deleted)?.length > 0 && (
        <div key={`${index}_${serviceType}`}>
          {serviceType ? (
            <h4 className={getPublicStyleByServiceCabapType(serviceType)}>
              {getServiceCapabTypeFromValue(serviceType)?.label}
            </h4>
          ) : null}
          <ul
            className={`${styles.list_default} ${styles.products_list}`}
            key={`${index}_${serviceType}`}
          >
            {a.level2
              ? a.level2.map(
                  (b, index) =>
                    capab
                      ?.filter((p) => p.area === a.level1 && !p.deleted)
                      .map((el) =>
                        el.products.filter((elm) => elm.level2 === b.name)
                      )
                      .filter((el) => el.length > 0)?.length > 0 && (
                      <li key={`${index}_${serviceType}_li`}>
                        {b.name}
                        <ul key={`${index}_${serviceType}_ul`}>
                          {b.level3
                            ? b.level3.map((c, index) => {
                                if (
                                  capab
                                    ?.filter((p) => p.area === a.level1)
                                    .map((el) =>
                                      el.products.filter(
                                        (elm) => elm.id === c.id
                                      )
                                    )
                                    .filter((el) => el.length > 0).length > 0
                                ) {
                                  return (
                                    <li
                                      className={styles.last_item}
                                      key={`${index}_${serviceType}_${c.id}`}
                                    >
                                      <ProductsTooltip
                                        content={getTooltipData(
                                          `${serviceType}_${b.name}_${c.name}`
                                        )}
                                      >
                                        {c.name}
                                      </ProductsTooltip>
                                    </li>
                                  );
                                }
                              })
                            : null}
                        </ul>
                      </li>
                    )
                )
              : null}
          </ul>
        </div>
      )
    );
  };

  return (
    <div className={styles.services}>
      {broadcomProducts?.length > 0 &&
      partner.capabilities.some((p) => !p.deleted) ? (
        <div>
          <h3>{t("solution_areas_expertise")}</h3>
          {partner.slug !== "mainframe" &&
          partner.slug !== "enterprise-software" &&
          partner.slug !== "symantec-cybersecurity" ? (
            broadcomProducts?.map((a, index) => {
              return (
                <div>
                  {partner.capabilities?.filter(
                    (p) => p.area === a.level1 && !p.deleted
                  )?.length > 0 ? (
                    <h4>{a.level1}</h4>
                  ) : null}
                  {serviceTypes?.length > 0
                    ? serviceTypes?.map((serviceType) =>
                        partner.capabilities?.filter(
                          (capab) => capab.serviceType === serviceType
                        )?.length > 0
                          ? getProdCapab({
                              capab: partner.capabilities?.filter(
                                (capab) => capab.serviceType === serviceType
                              ),
                              serviceType,
                              index,
                              a,
                            })
                          : getProdCapab({
                              capab: partner.capabilities,
                              serviceType,
                              index,
                              a,
                            })
                      )
                    : getProdCapab({
                        capab: partner.capabilities,
                        serviceType: null,
                        index,
                        a,
                      })}
                </div>
              );
            })
          ) : partner.slug === "mainframe" ? (
            <MainframeSolutionInfo />
          ) : partner.slug === "enterprise-software" ? (
            <EnterpriseSoftwareInfo />
          ) : (
            <CybersecurityInfo />
          )}
        </div>
      ) : null}
      {noEducation?.length > 0 ? (
        <div>
          <h3>{t("service_offerings")}</h3>
          <ul className={styles.list_default}>
            {noEducation.map((service, idx) => (
              <li key={`li_item${idx}`}>
                <ProductsTooltip content={getTooltipData(service.name, true)}>
                  {service.name}
                </ProductsTooltip>
              </li>
            ))}
          </ul>
          {education?.length > 0 ? (
            <div className={styles.education_offerings}>
              <h3>{t("education_offerings")}</h3>
              <ul className={styles.list_default}>
                {education.map((service) => (
                  <li>
                    <ProductsTooltip
                      content={getTooltipData(service.name, true)}
                    >
                      {service.name}
                    </ProductsTooltip>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
      {withUseCasesAndVerticals ? (
        <>
          {partner?.verticals?.length > 0 ? (
            <div>
              <h3>{t("verticals")}</h3>
              <ul className={styles.list_default}>
                {partner.verticals
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((vertical, index) => (
                    <li key={index}>{vertical?.name || ""}</li>
                  ))}
              </ul>
            </div>
          ) : null}
          {partner?.partnerUseCases?.length > 0 ? (
            <div>
              <h3>{t("customer_stories")}</h3>
              <ul className={styles.list_default}>
                {partner?.partnerUseCases.map((useCasesType, index) => (
                  <li key={index}>{useCasesType || ""}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
